import { useAtom, useAtomValue } from 'jotai';
import { Suspense, useEffect } from 'react';
import { twJoin } from 'tailwind-merge';
import { isMappedMetricPendingAtom } from '@/atoms/metric/metricFilterMapping';
import { visualisationConfigsAtom } from '@/atoms/queries/visualisation';
import { selectedVisualisationAtom } from '@/atoms/visualisation';
import { VISUALISATION } from '@/consts/searchKeys';
import { useGetPitchRotation } from '@/hooks/useGetPitchRotation';
import { useSelectedVisualisationCrop } from '@/hooks/usePitchCrop';
import { useQueryParam } from '@/hooks/useQueryParam';
import { VisualisationComponentProps, VisualisationMap } from '@/types/visualisation';
import { FallbackLoading } from '../FallbackLoading';

const VisualisationComponentWithData = ({
  allowInteraction = false,
  visualisations,
  rotationName,
  visibleArea,
}: VisualisationComponentProps & {
  visualisations: VisualisationMap;
}) => {
  const metricVisualisations = useAtomValue(visualisationConfigsAtom);
  const [selectedVisualisation, setSelectedVisualisation] = useAtom(selectedVisualisationAtom);
  const selectedVis = selectedVisualisation.visualisationKey;

  const visParam = useQueryParam(VISUALISATION);

  const selectedVisConfig = metricVisualisations.find(vis => vis.visualisationKey === visParam);
  useEffect(() => {
    if (visParam && selectedVisConfig) {
      setSelectedVisualisation(selectedVisConfig);
    }
  }, [visParam, !!selectedVisConfig]);

  // An unknown visualisation key from the API may not exist in the map so
  // fallback to the first available.
  const [fallbackKey] = Object.keys(visualisations) as (keyof VisualisationMap)[];
  const selectedVisKey = selectedVis as keyof VisualisationMap;
  const resolvedVis = visualisations[selectedVisKey] ? selectedVisKey : fallbackKey;
  const VisComponent = visualisations[resolvedVis];

  const isHorizontal = rotationName === 'HORIZONTAL' || rotationName === 'HORIZONTAL_REVERSE';
  const isFullPitch = visibleArea === 'FULL_PITCH';

  if (!VisComponent) {
    return null;
  }

  return (
    <div
      className={twJoin((!isHorizontal || !isFullPitch) && '[&_svg]:max-h-[600px]')}
      data-testid={`vis-component-${resolvedVis}`}
    >
      <VisComponent allowInteraction={allowInteraction} />
    </div>
  );
};

export const VisualisationComponent = ({
  allowInteraction,
  visualisations,
}: VisualisationComponentProps & { visualisations: VisualisationMap }) => {
  const pitchRotation = useGetPitchRotation(false);
  const { visibleCrop: visibleArea } = useSelectedVisualisationCrop();
  const isMappingPending = useAtomValue(isMappedMetricPendingAtom);
  const loading = <FallbackLoading isPreview={false} />;

  return (
    <div data-testid={`vis-orientation-${pitchRotation}`} className="w-full mx-auto max-w-[1000px]">
      <div data-testid={`vis-crop-${visibleArea}`}>
        <Suspense fallback={loading}>
          {isMappingPending ? (
            loading
          ) : (
            <VisualisationComponentWithData
              allowInteraction={allowInteraction}
              visualisations={visualisations}
              visibleArea={visibleArea}
              rotationName={pitchRotation}
            />
          )}
        </Suspense>
      </div>
    </div>
  );
};
